// On-demand harness profile for a scope: sample its most recent
// prompts, hydrate their raw payloads, and mine the scaffolding key
// set (lib/harnessTemplates). The trace detail overlay calls this when
// its Harness toggle flips on — one sample per scope, cached at module
// level, so flipping the toggle across traces in the same scope reuses
// the corpus instead of re-hydrating sixty payloads each time.
//
// The sample is "most recent N in scope", NOT the X-ray lens's
// scrubbable window — the overlay needs a stable, cheap corpus, not a
// navigable one. Sixty prompts mirrors the skill lens's scan budget.

import { query } from "./dataClient.js";
import { encodeScopePath } from "./dimensionDefs.js";
import { hydratePrompt } from "./triggers/triggerHarness.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import {
    mineHarnessTemplates,
    splitTemplateLines,
    inputSideText,
    boilerKeySet,
} from "./harnessTemplates.js";

// 120 (not the skill lens's 60): the corpus needs SESSION DIVERSITY —
// recent traffic is often a few dense agent sessions, and cross-session
// DF starves when the sample only spans two or three of them.
const SAMPLE_LIMIT = 120;
const CONCURRENCY = 6;

const cache = new Map(); // scope key → Promise<profile>
onAuthIdentityChange(() => cache.clear());

/**
 * @returns Promise<{
 *   boilerKeys: Set<string>, promptCount: number, sessionCount: number,
 *   threshold: number, templateCount: number,
 * }>
 */
export function loadHarnessProfile({ scopePath = [], range = null } = {}) {
    const epoch = getAuthIdentityEpoch();
    const rangeKey = range && typeof range === "object" ? `${range.from}-${range.to}` : String(range ?? "");
    const key = `${epoch}|${encodeScopePath(scopePath) || "org"}|${rangeKey}`;
    let p = cache.get(key);
    if (!p) {
        // A failed sample shouldn't poison the cache — drop it so the
        // next toggle retries.
        p = buildProfile({ scopePath, range }).then((profile) => {
            if (getAuthIdentityEpoch() !== epoch) {
                const error = new Error("Authentication context changed while loading the harness profile");
                error.name = "AuthContextChangedError";
                throw error;
            }
            return profile;
        }).catch((err) => {
            cache.delete(key);
            throw err;
        });
        cache.set(key, p);
    }
    return p;
}

async function buildProfile({ scopePath, range }) {
    const traces = await query("getTraces", { scopePath, range, limit: SAMPLE_LIMIT, sort: "newest" });
    const rows = (Array.isArray(traces) ? traces : []).filter((t) => t?.id);
    const docs = [];
    let next = 0;
    async function worker() {
        while (next < rows.length) {
            const row = rows[next++];
            try {
                const ev = await hydratePrompt(row.id);
                if (ev?.parsedConversation) {
                    const text = inputSideText(ev.parsedConversation.turns);
                    if (text) {
                        docs.push({
                            id: row.id,
                            sessionId: row.sessionId || "",
                            lines: splitTemplateLines(text),
                            chars: text.length,
                        });
                    }
                }
            } catch { /* skip rows that won't hydrate/parse */ }
        }
    }
    await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, Math.max(1, rows.length)) }, () => worker()),
    );
    const mined = mineHarnessTemplates(docs);
    return {
        boilerKeys: boilerKeySet(mined),
        promptCount: docs.length,
        sessionCount: mined.sessionCount,
        threshold: mined.threshold,
        templateCount: mined.templates.length,
    };
}
